/**
 * Pure helpers for the fact-suggestion pipeline: `computeRuleSuggestions` detects
 * phone/email/pet client-side (no LLM) so they can be persisted as pending rows,
 * `pendingRowSuggestions` reads the backend's pending rows for display, and
 * `computeExtractionRequest` decides when to request a server-side LLM run. Both
 * suggestion sources end up as backend rows and are shown from `pendingRowSuggestions`.
 * The lead's own columns are the source of truth for fact values: a set column is
 * never re-suggested.
 */

import type { ILead } from 'src/services/api';
import type { IMessage } from 'src/services/api/messages/types';
import { EMessageDirection } from 'src/services/api/messages/types';
import type { ILeadFactSuggestion, LeadFactKey } from 'src/services/api/leads/lead-facts';
import type { FactExtractionRequest } from 'src/services/api/leads/use-create-fact-extraction';
import { extractPhone, extractEmail, extractPetType } from 'src/services/lead-facts/extractors';
import { computeFactContext } from 'src/services/lead-facts/triggers';
import {
    FACT_LABELS,
    LLM_FACT_KEYS,
    type IFactSuggestion,
    type SpeakerMessage,
} from 'src/services/lead-facts/types';

/** Normalize a fact value for equality checks (case- and space-insensitive). */
function normalizeFactValue(value: string): string {
    return value.trim().toLowerCase();
}

/** The lead's current column value for a fact key (every key maps 1:1 to a column). */
export function leadFactValue(lead: ILead, key: LeadFactKey): string | null {
    const value = lead[key];
    return value === null || value === undefined ? null : String(value);
}

function suggestionByKey(
    suggestions: ILeadFactSuggestion[],
    key: LeadFactKey
): ILeadFactSuggestion | undefined {
    return suggestions.find((suggestion) => suggestion.key === key);
}

/** The conversation ordered oldest-first (the API does not guarantee order). */
function sortByTime(messages: IMessage[]): IMessage[] {
    return [...messages].sort(
        (a, b) => new Date(a.inserted_at).getTime() - new Date(b.inserted_at).getTime()
    );
}

function toSpeakerMessages(messages: IMessage[]): SpeakerMessage[] {
    return messages.map((m) => ({
        direction: m.direction,
        contents: m.contents ?? '',
    }));
}

/**
 * Whether `candidate` may be suggested for `key`: a set lead column is the accepted
 * truth and is never re-suggested; an accepted row is final; a rejected row
 * suppresses the same value but a genuinely different one may be re-suggested; a
 * pending row with the same value is already shown by its own stored chip.
 */
export function shouldSuggest(
    key: LeadFactKey,
    candidate: string,
    suggestions: ILeadFactSuggestion[],
    lead: ILead
): boolean {
    if (leadFactValue(lead, key) !== null) {
        return false;
    }
    const row = suggestionByKey(suggestions, key);
    if (!row) {
        return true;
    }
    if (row.status === 'accepted') {
        return false;
    }
    return normalizeFactValue(row.value) !== normalizeFactValue(candidate);
}

function lastNonNull<T>(extractor: (text: string) => T | null, texts: string[]): T | null {
    let found: T | null = null;
    for (const text of texts) {
        const value = extractor(text);
        if (value !== null) {
            found = value;
        }
    }
    return found;
}

const RULE_EXTRACTORS: [LeadFactKey, (text: string) => string | null][] = [
    ['phone_number', extractPhone],
    ['email', extractEmail],
    ['pet_type', extractPetType],
];

/**
 * Phone/email/pet detected from the lead's messages (no LLM). Detection itself is
 * client-side; the results are then persisted as pending suggestion rows by the
 * backend (see usePersistRegexSuggestions), not shown directly. Only values that
 * warrant a fresh suggestion are returned (see {@link shouldSuggest}).
 */
export function computeRuleSuggestions(
    messages: IMessage[],
    lead: ILead,
    suggestions: ILeadFactSuggestion[]
): IFactSuggestion[] {
    const leadTexts = sortByTime(messages)
        .filter((m) => m.direction === EMessageDirection.IN && m.contents)
        .map((m) => m.contents);

    const out: IFactSuggestion[] = [];
    for (const [key, extractor] of RULE_EXTRACTORS) {
        const value = lastNonNull(extractor, leadTexts);
        if (value !== null && shouldSuggest(key, value, suggestions, lead)) {
            out.push({
                key,
                value,
                label: FACT_LABELS[key],
                source: 'rule',
            });
        }
    }
    return out;
}

/** Pending suggestion rows resurface as chips (they survive a refresh without a new run). */
export function pendingRowSuggestions(
    suggestions: ILeadFactSuggestion[],
    lead: ILead
): IFactSuggestion[] {
    return suggestions
        .filter((suggestion) => suggestion.status === 'pending')
        /* a set column is the accepted truth, even when the row is stale */
        .filter((suggestion) => leadFactValue(lead, suggestion.key) === null)
        .map((suggestion) => ({
            key: suggestion.key,
            value: suggestion.value,
            label: FACT_LABELS[suggestion.key],
            source: 'stored' as const,
            id: suggestion.id,
        }));
}

/**
 * Whether the lead still warrants an LLM run: at least one LLM-extractable fact has
 * no column value and is neither accepted nor pending. A rejected suggestion still
 * warrants a run so a freshly extracted value can be compared against the rejected one.
 */
function needsLlm(suggestions: ILeadFactSuggestion[], lead: ILead): boolean {
    return LLM_FACT_KEYS.some((key) => {
        if (leadFactValue(lead, key) !== null) {
            return false;
        }
        const status = suggestionByKey(suggestions, key)?.status;
        return status !== 'accepted' && status !== 'pending';
    });
}

export const NO_EXTRACTION: FactExtractionRequest = { shouldCall: false, lastMessageUuid: '' };

/**
 * Whether to POST an extraction run and with what window: only when something
 * fact-relevant came up (the client trigger), an LLM-extractable field is still open,
 * and the newest message has a uuid for the server's idempotency guard. The window
 * hint reaches back to the oldest fact-bearing message; the server clamps it.
 */
export function computeExtractionRequest(
    messages: IMessage[],
    suggestions: ILeadFactSuggestion[],
    lead: ILead
): FactExtractionRequest {
    if (!needsLlm(suggestions, lead)) {
        return NO_EXTRACTION;
    }
    const sorted = sortByTime(messages);
    const indices = computeFactContext(toSpeakerMessages(sorted));
    if (indices.length === 0) {
        return NO_EXTRACTION;
    }
    const lastMessageUuid = sorted[sorted.length - 1]?.uuid;
    if (!lastMessageUuid) {
        return NO_EXTRACTION;
    }
    return {
        shouldCall: true,
        lastMessageUuid,
        windowSize: sorted.length - Math.min(...indices),
    };
}
